import { useEffect, useState } from "react";
import { Building2, Package, Files, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { useTransportadora, formatCNPJ as fmt } from "@/contexts/TransportadoraContext";

export default function Transportadoras() {
  const { transportadoras, loading, selectedCnpj, setSelectedCnpj } =
    useTransportadora();

  const onSelect = (cnpj) => {
    setSelectedCnpj(cnpj);
    window.location.reload();
  };

  return (
    <div data-testid="page-transportadoras" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Multi-tenant
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Transportadoras
          </h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            {transportadoras.length} transportadora(s) detectada(s) automaticamente
            a partir dos arquivos OCOREN processados. Clique em um card para
            filtrar todo o sistema apenas com os dados dela.
          </p>
        </div>
      </div>

      {loading && (
        <div className="text-center text-[#52525B] py-12">
          Carregando…
        </div>
      )}

      {!loading && transportadoras.length === 0 && (
        <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center text-[#52525B]">
          Nenhuma transportadora detectada ainda. Faça upload de um arquivo OCOREN
          para começar.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {transportadoras.map((t) => {
          const isSelected = selectedCnpj === t.cnpj;
          const total = t.total_movimentacoes || 1;
          const pctEntregue = ((t.total_entregas || 0) / total) * 100;
          const pctProblema = ((t.total_problemas || 0) / total) * 100;
          return (
            <button
              key={t.cnpj}
              data-testid={`transp-card-${t.cnpj}`}
              onClick={() => onSelect(t.cnpj)}
              className={`text-left bg-white border rounded-md p-5 transition-all hover:border-[#09090B] hover:shadow-sm ${
                isSelected ? "border-[#09090B] ring-2 ring-[#09090B]/10" : "border-[#E4E4E7]"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-[#09090B] rounded-md flex items-center justify-center shrink-0">
                    <Building2 size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-bold text-[#09090B] truncate">
                      {t.nome || "Sem nome"}
                    </h3>
                    <div className="text-[11px] font-mono-data text-[#52525B] mt-0.5">
                      {fmt(t.cnpj)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-[#09090B] font-semibold">
                  <span>Filtrar</span>
                  <ArrowRight size={12} />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#52525B] font-bold flex items-center gap-1">
                    <Files size={9} />
                    Arquivos
                  </div>
                  <div className="text-xl font-mono-data font-bold text-[#09090B] mt-0.5">
                    {(t.total_arquivos || 0).toLocaleString("pt-BR")}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#52525B] font-bold flex items-center gap-1">
                    <Package size={9} />
                    Eventos
                  </div>
                  <div className="text-xl font-mono-data font-bold text-[#09090B] mt-0.5">
                    {(t.total_movimentacoes || 0).toLocaleString("pt-BR")}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#52525B] font-bold flex items-center gap-1">
                    <CheckCircle2 size={9} />
                    Entregues
                  </div>
                  <div className="text-xl font-mono-data font-bold text-emerald-700 mt-0.5">
                    {(t.total_entregas || 0).toLocaleString("pt-BR")}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#52525B] font-bold flex items-center gap-1">
                    <AlertTriangle size={9} />
                    Problemas
                  </div>
                  <div className="text-xl font-mono-data font-bold text-rose-700 mt-0.5">
                    {(t.total_problemas || 0).toLocaleString("pt-BR")}
                  </div>
                </div>
              </div>

              {/* Barra de proporção entregue/problema */}
              <div className="mt-4 h-1.5 bg-[#FAFAFA] rounded-sm overflow-hidden flex">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${pctEntregue}%` }}
                  title={`${pctEntregue.toFixed(1)}% entregues`}
                />
                <div
                  className="h-full bg-rose-500"
                  style={{ width: `${pctProblema}%` }}
                  title={`${pctProblema.toFixed(1)}% problemas`}
                />
              </div>
              <div className="mt-1.5 flex justify-between text-[10px] font-mono-data text-[#52525B]">
                <span>{pctEntregue.toFixed(1)}% entregues</span>
                <span>{pctProblema.toFixed(1)}% problemas</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
